import File from "@/assets/doc/file.svg";
import { useContextBarAppearance } from "@/contexts/ContextBarAppearanceContext";
import { documentMimeTypes } from "@/domain/entities/emails/attachmentMime";
import DisplayAttachment from "@/domain/entities/emails/DisplayAttachment";
import LinkObject from "@/domain/entities/emails/LinkObject";
import Thread from "@/domain/entities/emails/Thread";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import docIcons from "../../docIcons";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
    NavigationMenu,
    NavigationMenuItem,
} from "../../ui/local-navigation-menu";
import { ScrollArea } from "../../ui/scroll-area";
import AttachmentItem from "./AttachementItems";
import DocumentPreview from "./DocumentPreview";
import LinkItem from "./LinkItem";
import NoItem from "./NoItem";

export enum DocumentsAndLinksVariant {
    THREAD = "thread",
    CUSTOM_VIEW = "customView",
}

interface DocumentsProps {
    documents?: DisplayAttachment[];
    links?: LinkObject[];
    mailsList?: Thread;
    variant?: DocumentsAndLinksVariant;
}

enum SelectionType {
    Documents = "documents",
    Links = "links",
}

const DocumentsLinksAndSimilar = ({
    documents,
    links,
    mailsList,
    variant = DocumentsAndLinksVariant.THREAD,
}: DocumentsProps) => {
    const { t } = useTranslation();
    const { isDeepFocus } = useContextBarAppearance();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentAttachment, setCurrentAttachment] =
        useState<DisplayAttachment | null>(null);

    const getInitialSelection = (): SelectionType => {
        if (documents && documents.length > 0) return SelectionType.Documents;
        if (links && links.length > 0) return SelectionType.Links;

        return SelectionType.Documents;
    };

    const [activeSelection, setActiveSelection] = useState<SelectionType>(
        getInitialSelection(),
    );

    useEffect(() => {
        setActiveSelection(getInitialSelection());
    }, [mailsList]);

    const handlePreviewOpen = (
        attachment: DisplayAttachment,
        index: number,
    ) => {
        setCurrentAttachment(attachment);
        setCurrentIndex(index);
        setIsPreviewOpen(true);
    };

    const handlePrevious = () => {
        if (documents && currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setCurrentAttachment(documents[currentIndex - 1]);
        }
    };

    const handleNext = () => {
        if (documents && currentIndex < documents.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setCurrentAttachment(documents[currentIndex + 1]);
        }
    };

    if (!documents && !links) {
        return null;
    }

    const renderIconLucide = (Icon: LucideIcon) => (
        <Icon className="mr-2 max-h-6 max-w-6 text-surface-invert" />
    );

    const getPreviewIcon = (attachment: DisplayAttachment) => {
        const Icon = docIcons[attachment.type as documentMimeTypes] || File;

        return typeof Icon === "string" ? (
            <img
                alt=""
                className="mr-2 max-h-6 max-w-6 text-surface-invert"
                src={Icon}
            />
        ) : (
            renderIconLucide(Icon)
        );
    };

    const renderDocuments = () => {
        if (documents?.length === 0) {
            return <NoItem text={t("widgets.documents.noDocuments")} />;
        }

        return documents?.map((doc, index) => (
            <AttachmentItem
                key={doc.key}
                attachment={doc}
                icon={getPreviewIcon(doc)}
                onPreviewOpen={() => handlePreviewOpen(doc, index)}
            />
        ));
    };

    const renderLinks = () => {
        if (links?.length === 0) {
            return <NoItem text={t("widgets.links.noLinks")} />;
        }

        return links?.map((link) => <LinkItem key={link.url} link={link} />);
    };

    return (
        <Card
            className={cn(
                "flex flex-col",
                isDeepFocus
                    ? "border border-white/10 bg-white/5 text-white"
                    : "bg-white",
            )}
            variant="white"
        >
            <CardHeader
                className={cn(
                    "border-b",
                    isDeepFocus ? "border-white/10" : "border-surface-divider",
                )}
            >
                <NavigationMenu>
                    {documents && (
                        <NavigationMenuItem
                            activeValue={activeSelection}
                            count={documents.length}
                            label={t("common.documents")}
                            onSelect={() =>
                                setActiveSelection(SelectionType.Documents)
                            }
                            value="documents"
                            variant={isDeepFocus ? "deepFocus" : "default"}
                        />
                    )}
                    {links && (
                        <NavigationMenuItem
                            activeValue={activeSelection}
                            count={links.length}
                            label={t("common.links")}
                            onSelect={() =>
                                setActiveSelection(SelectionType.Links)
                            }
                            value="links"
                            variant={isDeepFocus ? "deepFocus" : "default"}
                        />
                    )}
                </NavigationMenu>
            </CardHeader>
            <CardContent variant="ghost">
                {/* width specified to avoid content overflow on long documents titles */}
                <ScrollArea
                    className={isDeepFocus ? "w-100" : "w-80"}
                    viewportClassName={cn(
                        "[&>div]:!h-auto",
                        variant === DocumentsAndLinksVariant.THREAD
                            ? "max-h-55"
                            : "max-h-35",
                    )}
                >
                    {activeSelection === "documents" && renderDocuments()}
                    {activeSelection === "links" && renderLinks()}
                </ScrollArea>
            </CardContent>
            {currentAttachment && (
                <DocumentPreview
                    attachment={currentAttachment}
                    hasNext={currentIndex < (documents?.length ?? 0) - 1}
                    hasPrevious={currentIndex > 0}
                    icon={getPreviewIcon(currentAttachment)}
                    isOpen={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                />
            )}
        </Card>
    );
};

export default DocumentsLinksAndSimilar;
